import { createFileRoute, Link } from "@tanstack/react-router";
import { PageShell, PageHero } from "@/components/PageShell";
import { Reveal } from "@/components/Reveal";
import { ArrowRight, BookOpen } from "lucide-react";
import { RESOURCES, faqAnchor } from "@/lib/resources";
import { SITE_ORIGIN, sectionImageMeta, SECTION_OG } from "@/lib/ogSections";
import { seoDescription } from "@/lib/seoText";
import { hreflangLinks } from "@/lib/hreflang";
import { pageSchemaScript, PERSON_ID, PRACTICE_ID } from "@/seo/schema";

const URL = `${SITE_ORIGIN}/resources`;
const TITLE = "Design Resources & Benchmarks | Arif Khan";
/** One headline question per resource, answered on the hub and cited back to its source page. */
const HUB_FAQS = RESOURCES.flatMap((r) =>
  r.faqs.slice(0, 1).map((f) => ({
    ...f,
    resource: r.title,
    href: `/resources/${r.slug}#${faqAnchor(f.q)}`,
  })),
);

const DESCRIPTION = seoDescription(
  "Free reference guides on perimeter protection benchmarks, smart automation, luxury home cinema, CCTV command centres and auditorium AV — written for architects and owners.",
);

export const Route = createFileRoute("/resources/")({
  component: Page,
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: URL },
      { property: "og:type", content: "website" },
      ...sectionImageMeta(SECTION_OG.solutions, "Design resources and benchmarks by Arif Khan"),
    ],
    links: [{ rel: "canonical", href: URL }, ...hreflangLinks("/resources")],
    scripts: [
      pageSchemaScript(
        {
          path: "/resources",
          type: "CollectionPage",
          name: TITLE,
          description: DESCRIPTION,
          breadcrumb: [{ name: "Resources" }],
          extra: {
            about: { "@id": PERSON_ID },
            hasPart: RESOURCES.map((r) => ({
              "@type": "TechArticle",
              "@id": `${SITE_ORIGIN}/resources/${r.slug}#article`,
              headline: r.title,
              url: `${SITE_ORIGIN}/resources/${r.slug}`,
              description: r.description,
              datePublished: r.updated,
              dateModified: r.updated,
              author: { "@id": PERSON_ID },
              publisher: { "@id": PRACTICE_ID },
            })),
          },
        },
        [
          {
            "@type": "FAQPage",
            "@id": `${URL}#faq`,
            url: URL,
            name: "Design resources — frequently asked questions",
            inLanguage: "en-IN",
            isPartOf: { "@id": `${URL}#webpage` },
            mainEntity: HUB_FAQS.map((f) => ({
              "@type": "Question",
              "@id": `${SITE_ORIGIN}${f.href}`,
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a, url: `${SITE_ORIGIN}${f.href}` },
            })),
          },
        ],
      ),
    ],
  }),
});

function Page() {
  return (
    <PageShell>
      <PageHero
        eyebrow="Resources"
        title={<>Benchmarks & reference guides</>}
        description="Citable, vendor-neutral references drawn from 23+ years of practice and 500+ km of perimeter designed and commissioned. Free to use, quote and link — attribution appreciated."
      />

      <section className="py-24">
        <div className="container-page grid gap-8 md:grid-cols-2">
          {RESOURCES.map((r, i) => (
            <Reveal key={r.slug} delay={i * 60}>
              <Link
                to="/resources/$slug"
                params={{ slug: r.slug }}
                className="group block h-full rounded-2xl border border-border bg-surface overflow-hidden transition-colors hover:border-foreground/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <div className="aspect-[16/9] overflow-hidden">
                  <img
                    src={r.image}
                    alt={r.title}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
                <div className="p-7">
                  <span className="eyebrow">{r.eyebrow}</span>
                  <h2 className="mt-3 text-2xl font-semibold tracking-tight">{r.title}</h2>
                  <p className="mt-3 text-ink-soft leading-relaxed">{r.description}</p>
                  <span className="mt-5 inline-flex items-center gap-2 text-sm font-medium">
                    Read the reference
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      <section id="faq" className="py-20 border-t border-border">
        <div className="container-page max-w-3xl">
          <span className="eyebrow">Answers</span>
          <h2 className="mt-4 text-2xl md:text-3xl font-semibold tracking-tight">
            Frequently asked questions
          </h2>
          <dl className="mt-8 divide-y divide-border border-y border-border">
            {HUB_FAQS.map((f) => (
              <div key={f.q} className="py-6">
                <dt className="text-base md:text-lg font-medium">{f.q}</dt>
                <dd className="mt-2 text-sm md:text-base text-ink-soft leading-relaxed">
                  {f.a}{" "}
                  <a
                    href={f.href}
                    className="underline underline-offset-4 hover:no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    Source: {f.resource}
                  </a>
                </dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      <section className="py-20 border-t border-border bg-surface">
        <div className="container-page flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
          <div className="flex items-start gap-4 max-w-2xl">
            <BookOpen className="mt-1 h-6 w-6 shrink-0" />
            <p className="text-ink-soft leading-relaxed">
              Writing an article, standard or tender that draws on this material? Quote it freely with a
              link back to the source page — and tell us, so we can share the piece.
            </p>
          </div>
          <Link
            to="/contact"
            aria-label="Let's Talk — start a project conversation"
            className="inline-flex items-center gap-2 rounded-full border border-foreground px-6 py-3 text-sm font-medium transition-colors hover:bg-foreground hover:text-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            Let's Talk <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>
    </PageShell>
  );
}
